import { useStore, useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "app/store";
import { actions } from "features/creator/slice";
import { updateProjectContent } from "features/creator/api";
import { recordEditHistory } from "features/creator/editHistory";
import {
  ActionCommand,
  ProjectEditAction,
  ActionCommandType,
  HistoryStackLabel,
  ClipboardTargetType,
} from "features/creator/types";

export const useActionCommand = () => {
  const store = useStore<RootState>();
  const dispatch = useDispatch<AppDispatch>();
  const active = useSelector((state: RootState) => state.user.active);

  // 操作の名前（変更履歴と undo の履歴の両方に使う）。名前は操作する前の state から引く
  const labelOf = (command: ActionCommand): (HistoryStackLabel & { count?: number }) | null => {
    const { creator } = store.getState();
    const propertyOf = (id: string) => creator.properties.entities[id];
    const screenName = (id: string) => propertyOf(id)?.property?.name ?? null;
    switch (command.type) {
      case ActionCommandType.ADD_COMPONENT:
        return { action_type: ProjectEditAction.ADD_COMPONENT, target_type: command.componentType };
      case ActionCommandType.REMOVE_COMPONENT:
        return {
          action_type: ProjectEditAction.REMOVE_COMPONENT,
          target_type: propertyOf(command.componentId)?.typeId,
          target_name: propertyOf(command.componentId)?.property?.name,
        };
      case ActionCommandType.REMOVE_GROUPING:
        return {
          action_type: ProjectEditAction.REMOVE_COMPONENT,
          count: command.componentIds.length,
        };
      case ActionCommandType.ADD_SCREEN:
        return { action_type: ProjectEditAction.ADD_SCREEN, target_type: "screen" };
      case ActionCommandType.REMOVE_SCREEN:
        return {
          action_type: ProjectEditAction.REMOVE_SCREEN,
          target_type: "screen",
          target_name: screenName(command.screenId),
        };
      case ActionCommandType.CHANGE_PROPERTY:
        return {
          action_type: ProjectEditAction.CHANGE_PROPERTY,
          target_type: propertyOf(command.componentId)?.typeId,
          target_name: command.property?.name ?? propertyOf(command.componentId)?.property?.name,
        };
      case ActionCommandType.CHANGE_BLOCKLY:
        // 画面を開いた・切り替えたときにも Blockly のイベントが遅れて届く。中身が変わっていなければ残さない
        if (creator.blockly.entities[command.screenId]?.xmlText === command.text) return null;
        return {
          action_type: ProjectEditAction.CHANGE_BLOCKLY,
          target_type: "block",
          target_name: screenName(command.screenId),
        };
      case ActionCommandType.PASTE:
        return { action_type: ProjectEditAction.PASTE };
      default:
        // CHANGE_CANVAS（画面の拡大・移動）と COPY は作品を変えないので残さない
        return null;
    }
  };

  // 変更履歴に残す（何をしたかだけ）。undo / redo は、取り消す・やり直す操作の名前を付けて残す
  const record = (command: ActionCommand) => {
    const { creator } = store.getState();
    const add = (entry: {
      action_type: ProjectEditAction;
      undone_action_type?: ProjectEditAction | null;
      target_type?: string | null;
      target_name?: string | null;
      count?: number;
    }) => recordEditHistory(active?.uid, creator.editingProjectId, entry);
    if (command.type === ActionCommandType.UNDO || command.type === ActionCommandType.REDO) {
      const isUndo = command.type === ActionCommandType.UNDO;
      const stacks = isUndo ? creator.undoStacks : creator.redoStacks;
      const label = stacks[stacks.length - 1]?.label;
      add({
        action_type: isUndo ? ProjectEditAction.UNDO : ProjectEditAction.REDO,
        undone_action_type: label?.action_type ?? null,
        target_type: label?.target_type,
        target_name: label?.target_name,
      });
      return null;
    }
    const label = labelOf(command);
    if (label) add(label);
    return label;
  };

  const handleAction = (command: ActionCommand) => {
    const label = record(command);
    const topBefore = store.getState().creator.undoStacks.at(-1);
    runCommand(command);
    // この操作で undo の履歴が積まれたら、操作の名前を付ける（undo を記録に残すときに何を取り消したかを書く）
    const topAfter = store.getState().creator.undoStacks.at(-1);
    if (label && topAfter && topAfter !== topBefore) {
      const { count: _count, ...stackLabel } = label;
      dispatch(actions.labelLastUndo(stackLabel));
    }
  };

  const runCommand = (command: ActionCommand) => {
    switch (command.type) {
      case ActionCommandType.ADD_COMPONENT:
        dispatch(
          actions.addComponent({
            type: command.componentType,
            category: command.category,
            screenId: command.screenId,
            position: command.position,
            image: command.image,
            drawing: command.drawing,
          }),
        );
        dispatch(actions.handleMaterialPanelCollapse(true));
        dispatch(updateProjectContent({ uid: active.uid }));
        break;
      case ActionCommandType.REMOVE_COMPONENT:
        dispatch(
          actions.removeComponent({
            screenId: command.screenId,
            componentId: command.componentId,
          }),
        );
        dispatch(updateProjectContent({ uid: active.uid }));
        break;
      case ActionCommandType.ADD_SCREEN:
        dispatch(actions.addScreen());
        dispatch(updateProjectContent({ uid: active.uid }));
        break;
      case ActionCommandType.REMOVE_SCREEN:
        dispatch(actions.removeScreen(command.screenId));
        dispatch(updateProjectContent({ uid: active.uid }));
        break;
      case ActionCommandType.REMOVE_GROUPING:
        dispatch(
          actions.removeGroupComponents({
            screenId: command.screenId,
            componentIds: command.componentIds,
          }),
        );
        dispatch(updateProjectContent({ uid: active.uid }));
        break;
      case ActionCommandType.CHANGE_CANVAS:
        dispatch(
          actions.updateCanvas({
            id: command.screenId,
            newCanvas: command.canvas,
          }),
        );
        dispatch(updateProjectContent({ uid: active.uid }));
        break;
      case ActionCommandType.CHANGE_BLOCKLY:
        dispatch(
          actions.updateBlockly({
            screenId: command.screenId,
            xmlText: command.text,
          }),
        );
        dispatch(updateProjectContent({ uid: active.uid }));
        break;
      case ActionCommandType.CHANGE_PROPERTY:
        dispatch(
          actions.updateProperty({
            id: command.componentId,
            newProperty: command.property,
            record: command.record,
          }),
        );
        dispatch(updateProjectContent({ uid: active.uid }));
        break;
      case ActionCommandType.COPY:
        switch (command.targetType) {
          case ClipboardTargetType.SCREEN:
            dispatch(actions.handleCopyScreen());
            break;
          case ClipboardTargetType.COMPONENT:
            dispatch(actions.handleCopyComponent());
            break;
          case ClipboardTargetType.GROUPING:
            dispatch(actions.handleCopyGrouping());
            break;
          default:
            break;
        }
        break;
      case ActionCommandType.PASTE:
        switch (command.targetType) {
          case ClipboardTargetType.SCREEN:
            dispatch(actions.handlePasteScreen());
            break;
          case ClipboardTargetType.COMPONENT:
            dispatch(actions.handlePasteComponent());
            break;
          case ClipboardTargetType.GROUPING:
            dispatch(actions.handlePasteGrouping());
            break;
          default:
            break;
        }
        dispatch(updateProjectContent({ uid: active.uid }));
        break;
      case ActionCommandType.UNDO:
        dispatch(actions.handleUndo());
        dispatch(updateProjectContent({ uid: active.uid }));
        break;
      case ActionCommandType.REDO:
        dispatch(actions.handleRedo());
        dispatch(updateProjectContent({ uid: active.uid }));
        break;
      default:
        break;
    }
  };
  return handleAction;
};
